import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../utils/api'

export interface CompanyFeatures {
  multiCompanyEnabled: boolean
  linkedCompanyIds: string[]
  effectiveCompanyIds: string[]
  applicationName: string
}

export const useCompanyFeatures = (isAuthenticated: boolean) => {
  const [features, setFeatures] = useState<CompanyFeatures>({
    multiCompanyEnabled: false,
    linkedCompanyIds: [],
    effectiveCompanyIds: [],
    applicationName: 'Workbeam'
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  const fetchFeatures = useCallback(async () => {
    if (!isAuthenticated) {
      setFeatures({
        multiCompanyEnabled: false,
        linkedCompanyIds: [],
        effectiveCompanyIds: [],
        applicationName: 'Workbeam'
      })
      return
    }

    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ data: CompanyFeatures }>('/settings/features')
      if (!isMountedRef.current) return
      const data = res.data?.data ?? res.data
      if (data) {
        setFeatures({
          multiCompanyEnabled: data.multiCompanyEnabled ?? false,
          linkedCompanyIds: Array.isArray(data.linkedCompanyIds) ? data.linkedCompanyIds : [],
          effectiveCompanyIds: Array.isArray(data.effectiveCompanyIds) ? data.effectiveCompanyIds : [],
          applicationName: typeof data.applicationName === 'string' && data.applicationName.trim()
            ? data.applicationName
            : 'Workbeam'
        })
      }
    } catch (err) {
      if (!isMountedRef.current) return
      console.error('Failed to fetch company features:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch features')
      setFeatures({
        multiCompanyEnabled: false,
        linkedCompanyIds: [],
        effectiveCompanyIds: [],
        applicationName: 'Workbeam'
      })
    } finally {
      if (isMountedRef.current) setLoading(false)
    }
  }, [isAuthenticated])

  const lastFetchRef = useRef<number>(0)
  const FEATURES_DEDUP_MS = 400

  useEffect(() => {
    const now = Date.now()
    if (now - lastFetchRef.current < FEATURES_DEDUP_MS) return
    lastFetchRef.current = now
    fetchFeatures()
  }, [fetchFeatures])

  return {
    ...features,
    loading,
    error,
    refetch: fetchFeatures
  }
}
